import { useEffect, useRef, useState } from 'react';
import { Files, Minimize2, PanelLeftOpen } from 'lucide-react';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  type ImperativePanelHandle,
} from 'react-resizable-panels';
import { cn } from '@/shared/cn';
import { HelpAnchor } from '@/design-system/HelpAnchor';
import { Commentable } from '@/shared/comments/Commentable';
import { HEADER_ICON_BUTTON } from '@/shared/ui-class';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { ChatComposer } from './ChatComposer';
import { ChatHeader } from './ChatHeader';
import { ChatMessages } from './ChatMessages';
import { FilesPane } from './FilesPane';
import { NewConversationButton } from './NewConversationButton';
import { RailHistoryOverlay } from './RailHistoryOverlay';
import { WelcomeState } from './WelcomeState';

const FILES_DEFAULT_PERCENT = 34;
const CHAT_MIN_SIZE = 35;
const FILES_MIN_SIZE = 24;

type ChatShellLayout = 'chat-home' | 'rail';

type ChatShellProps = {
  helpDoc?: string;
  helpLabel?: string;
  /** When set, shows project scope hint in the header (project assistant). */
  scopeLabel?: string;
  /** When true, the minimize control collapses the docked app-shell rail. */
  showMinimize?: boolean;
  onMinimize?: () => void;
  storageKey?: string;
  /**
   * `chat-home` — full-width `/` and project chat: floating new-chat pen,
   * no in-panel history chrome (left sidebar owns thread history).
   * `rail` — docked AI rail: inline header with history + new chat + title.
   */
  layout?: ChatShellLayout;
};

export function ChatShell({
  helpDoc = 'workspace/chat-rail',
  helpLabel = 'Chat',
  scopeLabel,
  showMinimize = false,
  onMinimize,
  storageKey = 'mock-ui:rail-chat-files',
  layout = 'rail',
}: ChatShellProps) {
  const { messages, setDraft } = useChat();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const filesPanelRef = useRef<ImperativePanelHandle | null>(null);
  const filesOpenRef = useRef(filesOpen);
  filesOpenRef.current = filesOpen;

  const isWelcome = messages.length === 0;
  const isChatHome = layout === 'chat-home';

  useEffect(() => {
    const handle = filesPanelRef.current;
    if (!handle) return;
    if (filesOpen && handle.isCollapsed()) {
      handle.expand(FILES_DEFAULT_PERCENT);
    } else if (!filesOpen && !handle.isCollapsed()) {
      handle.collapse();
    }
  }, [filesOpen]);

  return (
    <Commentable name="ChatShell">
      <div
        className="relative flex h-full min-h-0 w-full flex-col bg-[var(--background)]"
        style={{ ['--thread-max-width' as string]: '48rem' }}
      >
        {isChatHome ? (
          <>
            <div className="absolute left-12 top-3 z-30 flex items-center gap-1 md:left-3">
              <NewConversationButton variant="floating" />
            </div>
            <div className="absolute right-14 top-3 z-30 flex items-center gap-1">
              {!filesOpen ? (
                <button
                  type="button"
                  aria-label="Open file library"
                  title="Open file library"
                  onClick={() => setFilesOpen(true)}
                  className={cn(HEADER_ICON_BUTTON, 'flex-shrink-0')}
                >
                  <Files className="h-4 w-4" strokeWidth={1.6} />
                </button>
              ) : null}
              <HelpAnchor doc={helpDoc} label={helpLabel} />
            </div>
            <ChatHeader layout="floating" />
          </>
        ) : null}

        <PanelGroup
          autoSaveId={storageKey}
          direction="horizontal"
          className="flex min-h-0 w-full flex-1"
        >
          <Panel
            id="chat"
            defaultSize={100 - FILES_DEFAULT_PERCENT}
            minSize={CHAT_MIN_SIZE}
            className="flex h-full min-h-0 min-w-0 flex-col"
          >
            {!isChatHome ? (
              <header className="flex h-12 flex-shrink-0 items-center justify-between gap-2 px-3">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <button
                    type="button"
                    aria-label="Chat history"
                    title="Chat history"
                    onClick={() => setHistoryOpen(true)}
                    className={cn(HEADER_ICON_BUTTON, 'flex-shrink-0')}
                  >
                    <PanelLeftOpen className="h-4 w-4 flex-shrink-0" strokeWidth={1.6} />
                  </button>
                  <NewConversationButton />
                  <ChatHeader layout="inline" />
                  <HelpAnchor doc={helpDoc} label={helpLabel} />
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {scopeLabel ? (
                    <span className="hidden truncate text-xs text-muted sm:inline">
                      {scopeLabel}
                    </span>
                  ) : null}
                  {!filesOpen ? (
                    <button
                      type="button"
                      aria-label="Open file library"
                      title="Open file library"
                      onClick={() => setFilesOpen(true)}
                      className={cn(HEADER_ICON_BUTTON, 'flex-shrink-0')}
                    >
                      <Files className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                  ) : null}
                  {showMinimize ? (
                    <button
                      type="button"
                      aria-label="Minimize"
                      title="Minimize"
                      onClick={onMinimize}
                      className={cn(HEADER_ICON_BUTTON, 'flex-shrink-0')}
                    >
                      <Minimize2 className="h-4 w-4" strokeWidth={1.6} />
                    </button>
                  ) : null}
                </div>
              </header>
            ) : null}

            <div className="relative flex min-h-0 flex-1 flex-col">
              {isWelcome ? (
                <WelcomeState onStarterSelect={setDraft} />
              ) : (
                <>
                  {isChatHome ? <div className="h-14 flex-shrink-0" aria-hidden /> : null}
                  <ChatMessages />
                  <ChatComposer />
                </>
              )}
              {!isChatHome ? (
                <RailHistoryOverlay open={historyOpen} onClose={() => setHistoryOpen(false)} />
              ) : null}
            </div>
          </Panel>

          <PanelResizeHandle className="group relative w-px bg-[var(--glass-border)] data-[panel-group-direction=horizontal]:flex">
            <div className="pointer-events-none absolute inset-y-0 -inset-x-1.5 cursor-col-resize transition-colors group-hover:bg-zinc-300/60 dark:group-hover:bg-zinc-700/40" />
          </PanelResizeHandle>

          <Panel
            id="files"
            ref={filesPanelRef}
            defaultSize={0}
            minSize={FILES_MIN_SIZE}
            collapsible
            collapsedSize={0}
            onResize={() => {
              const handle = filesPanelRef.current;
              if (!handle) return;
              if (filesOpenRef.current && handle.isCollapsed()) {
                setFilesOpen(false);
              }
            }}
            className="flex min-h-0 min-w-0 flex-col"
          >
            {filesOpen ? <FilesPane onClose={() => setFilesOpen(false)} /> : null}
          </Panel>
        </PanelGroup>
      </div>
    </Commentable>
  );
}
